// Utility wrapper for starting Sentry transactions with custom sampling context.
// This lets you force specific transactions to be sampled regardless of the default sampling rate.
//
// Usage:
// import { startSentryTransaction } from './sentryTransaction';
// const txn = startSentryTransaction('expensive_generation', { op: 'job.generate', forceCapture: true });
// try {
//   // ... code you want to measure
//   txn?.setStatus('ok');
// } catch (e) {
//   txn?.setStatus('internal_error');
//   Sentry.captureException(e);
// } finally {
//   txn?.finish();
// }
//
// In tracesSampler (configured in index.tsx) we check customSamplingContext.force_capture
// and bump sampling to 1.0 for those transactions.

import * as Sentry from "@sentry/react";

/**
 * Start a Sentry transaction with optional force sampling.
 * @param {string} name - Human-readable transaction name.
 * @param {Object} options - Additional options.
 * @param {string} [options.op] - Short operation identifier (e.g., 'api.call', 'job.generate').
 * @param {boolean} [options.forceCapture=false] - If true, ensures sampling probability = 1.0.
 * @param {Object} [options.data] - Extra data to attach to the transaction.
 * @returns {import('@sentry/types').Transaction | undefined}
 */
export function startSentryTransaction(name, { op, forceCapture = false, data } = {}) {
  if (!Sentry.getCurrentHub) return; // guard if Sentry not initialized
  try {
    const transaction = Sentry.startTransaction(
      {
        name,
        op,
        metadata: {
          // mark that we explicitly requested capture (auxiliary marker)
          forced: forceCapture
        },
        data
      },
      {
        // Provide custom sampling context that tracesSampler can read.
        customSamplingContext: { force_capture: forceCapture }
      }
    );
    Sentry.getCurrentHub().configureScope((scope) => {
      scope.setSpan(transaction);
    });
    return transaction;
  } catch (e) {
    // fail silently – starting a transaction should never crash the app
    return undefined;
  }
}

/**
 * Convenience helper to wrap an async function inside a Sentry transaction.
 * @param {string} name
 * @param {Function} fn async or sync function to execute.
 * @param {Object} options same as startSentryTransaction
 */
export async function withSentryTransaction(name, fn, options) {
  const txn = startSentryTransaction(name, options);
  try {
    const result = await fn();
    txn && txn.setStatus("ok");
    return result;
  } catch (err) {
    txn && txn.setStatus("internal_error");
    Sentry.captureException(err);
    throw err;
  } finally {
    txn && txn.finish();
  }
}
